<template>
  <ContentWrap>
    <el-tabs v-model="activeTab" class="mb-12px" @tab-change="onTabChange">
      <el-tab-pane label="待审核主播" name="pending" />
      <el-tab-pane label="已关联主播" name="approved" />
      <el-tab-pane label="已拒绝" name="rejected" />
    </el-tabs>
    <el-form :inline="true" class="-mb-15px">
      <el-form-item>
        <el-button @click="getList"><Icon class="mr-5px" icon="ep:refresh" />刷新</el-button>
        <el-button type="primary" @click="openBind">
          <Icon class="mr-5px" icon="ep:plus" />绑定主播
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="主播" min-width="180">
        <template #default="{ row }">
          <div class="flex items-center gap-8px">
            <el-avatar :size="36" :src="row.anchorAvatar || undefined">
              {{ (row.anchorNickname || '?').slice(0, 1) }}
            </el-avatar>
            <div>
              <div>{{ row.anchorNickname || '—' }}</div>
              <div class="text-12px text-gray-400">ID {{ row.anchorUserId }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="银行卡号" prop="bankAccount" min-width="140" show-overflow-tooltip />
      <el-table-column label="粉丝量" prop="fansCount" width="100" />
      <el-table-column label="协议" prop="agreementTitle" min-width="120" show-overflow-tooltip>
        <template #default="{ row }">{{ row.agreementTitle || '—' }}</template>
      </el-table-column>
      <el-table-column label="合作次数" prop="coopCount" width="100" />
      <el-table-column label="结算金额" width="120">
        <template #default="{ row }">
          {{ row.settleAmount != null ? row.settleAmount : '—' }}
        </template>
      </el-table-column>
      <el-table-column label="关联时间" prop="bindTime" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row.id)">查看</el-button>
          <template v-if="row.status === 0">
            <el-button link type="success" @click="handleAudit(row.id, true)">通过</el-button>
            <el-button link type="danger" @click="handleAudit(row.id, false)">拒绝</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-dialog v-model="bindVisible" title="绑定主播" width="480px" @closed="resetBindForm">
    <el-form ref="bindFormRef" :model="bindForm" :rules="bindRules" label-width="100px">
      <el-form-item label="主播用户" prop="anchorUserId">
        <LiveAnchorUserSelect
          v-model="bindForm.anchorUserId"
          select-class="!w-full"
          :status="undefined"
          placeholder="搜索主播昵称 / 姓名 / 编号，或点选下方列表"
        />
      </el-form-item>
      <el-form-item label="收款账户" prop="bankAccount">
        <el-input v-model="bindForm.bankAccount" clearable placeholder="银行卡号或收款账号" />
      </el-form-item>
      <el-form-item label="合作协议" prop="agreementId">
        <el-select
          v-model="bindForm.agreementId"
          class="!w-full"
          clearable
          filterable
          placeholder="可选，选择店铺协议"
        >
          <el-option
            v-for="item in agreementOptions"
            :key="item.id"
            :label="item.title"
            :value="item.id!"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="bindVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="submitBind">确定</el-button>
    </template>
  </el-dialog>

  <el-drawer v-model="detailVisible" title="主播详情" size="420px">
    <el-skeleton v-if="detailLoading" :rows="8" animated />
    <el-descriptions v-else-if="detail" :column="1" border>
      <el-descriptions-item label="编号">{{ detail.id }}</el-descriptions-item>
      <el-descriptions-item label="主播">
        <div class="flex items-center gap-8px">
          <el-avatar :size="32" :src="detail.anchorAvatar || undefined">
            {{ (detail.anchorNickname || '?').slice(0, 1) }}
          </el-avatar>
          <span>{{ detail.anchorNickname || '—' }}（{{ detail.anchorUserId }}）</span>
        </div>
      </el-descriptions-item>
      <el-descriptions-item label="粉丝量">{{ detail.fansCount ?? 0 }}</el-descriptions-item>
      <el-descriptions-item label="银行卡号">{{ detail.bankAccount || '—' }}</el-descriptions-item>
      <el-descriptions-item label="协议">{{ detail.agreementTitle || '—' }}</el-descriptions-item>
      <el-descriptions-item label="协议摘要">{{
        detail.agreementContent || '—'
      }}</el-descriptions-item>
      <el-descriptions-item label="合作次数">{{ detail.coopCount ?? 0 }}</el-descriptions-item>
      <el-descriptions-item label="结算金额">{{ detail.settleAmount ?? '—' }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
      <el-descriptions-item label="关联时间">{{
        detail.bindTime ? formatDate(detail.bindTime) : '—'
      }}</el-descriptions-item>
    </el-descriptions>
    <el-empty v-else description="未找到主播关联" />
  </el-drawer>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import * as LiveApi from '@/api/merchant/live'
import { dateFormatter, formatDate } from '@/utils/formatTime'
import LiveAnchorUserSelect from '@/views/live/components/LiveAnchorUserSelect.vue'

defineOptions({ name: 'MerchantLiveAnchor' })

const message = useMessage()
const loading = ref(false)
const submitting = ref(false)
const list = ref<LiveApi.LiveShopAnchorVO[]>([])
const total = ref(0)
const activeTab = ref('pending')
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  status: 0 as number | undefined
})

const agreementOptions = ref<LiveApi.LiveAgreementVO[]>([])
const bindVisible = ref(false)
const bindFormRef = ref<FormInstance>()
const bindForm = reactive<{
  anchorUserId?: number
  bankAccount: string
  agreementId?: number
}>({
  anchorUserId: undefined,
  bankAccount: '',
  agreementId: undefined
})
const bindRules: FormRules = {
  anchorUserId: [{ required: true, message: '请选择主播用户', trigger: 'change' }]
}

const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<LiveApi.LiveShopAnchorVO | null>(null)

const statusLabel = (status?: number) => {
  if (status === 0) return '待审核'
  if (status === 1) return '已通过'
  if (status === 2) return '已拒绝'
  return status != null ? String(status) : '—'
}

const tabStatus = (tab: string): number => {
  if (tab === 'approved') return 1
  if (tab === 'rejected') return 2
  return 0
}

const onTabChange = () => {
  queryParams.pageNo = 1
  queryParams.status = tabStatus(activeTab.value)
  getList()
}

const getList = async () => {
  loading.value = true
  try {
    const data = await LiveApi.getLiveAnchorPage({
      status: queryParams.status,
      pageNo: queryParams.pageNo,
      pageSize: queryParams.pageSize
    })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

const loadAgreements = async () => {
  const data = await LiveApi.getLiveAgreementList()
  agreementOptions.value = Array.isArray(data) ? data : []
}

const openBind = async () => {
  await loadAgreements()
  bindVisible.value = true
}

const resetBindForm = () => {
  bindForm.anchorUserId = undefined
  bindForm.bankAccount = ''
  bindForm.agreementId = undefined
  bindFormRef.value?.clearValidate()
}

const submitBind = async () => {
  await bindFormRef.value?.validate()
  submitting.value = true
  try {
    await LiveApi.bindLiveAnchor({
      anchorUserId: bindForm.anchorUserId,
      bankAccount: bindForm.bankAccount,
      agreementId: bindForm.agreementId
    })
    message.success('已提交绑定，等待审核')
    bindVisible.value = false
    activeTab.value = 'pending'
    queryParams.status = 0
    await getList()
  } finally {
    submitting.value = false
  }
}

const handleAudit = async (id: number, pass: boolean) => {
  try {
    await message.confirm(pass ? '确认通过该主播关联？' : '确认拒绝该主播关联？')
  } catch {
    return
  }
  await LiveApi.auditLiveAnchor(id, pass)
  message.success(pass ? '已通过' : '已拒绝')
  await getList()
}

const openDetail = async (id?: number) => {
  if (!id) return
  detailVisible.value = true
  detailLoading.value = true
  detail.value = null
  try {
    detail.value = await LiveApi.getLiveAnchor(id)
  } finally {
    detailLoading.value = false
  }
}

onMounted(() => {
  queryParams.status = 0
  getList()
})
</script>
